import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import type { PreviewGroup } from "../../types";

/**
 * One question, read-only — « Pré-auth/Ligne question » (Figma `522:28554`).
 *
 * The frame's note is the contract: *the wording must be identical to what the wizard shows, because
 * this list is a READ of the round's question bank, not a hand-maintained duplicate. If the Aperçu
 * and the wizard diverge, that is a data bug.* So nothing here is translated locally — every string
 * arrives already localized from the round's frozen configuration.
 *
 * Nothing is clickable. There is no target, no hover, no focus ring: the applicant is reading.
 */
function QuestionRow({
  label,
  isRequired,
}: {
  label: string;
  isRequired: boolean;
}) {
  const { t } = useTranslation("acquire");

  return (
    <li className="flex items-start gap-2.5 py-1.5">
      {/*
        The wizard's own "not answered yet" bullet. Deliberately not a check and not a filled dot —
        either would claim something had already been done, on a page where nothing can be.
      */}
      <span aria-hidden className="mt-0.5 shrink-0 text-(--text-tertiary)">
        <Icon name="circle-dashed" size={16} />
      </span>
      <span className="min-w-0 flex-1">
        {/* `wrap-break-word` because a question can carry an unbreakable token — a URL — and 320px is
            where that overflows first. */}
        <span className="block wrap-break-word text-sm-regular text-(--text-primary)">
          {label}
        </span>
        {isRequired ? null : (
          <span className="block text-xs-regular text-(--text-tertiary)">
            {t("preview.dossier.optional")}
          </span>
        )}
      </span>
    </li>
  );
}

/**
 * One of the four tabs, collapsed to a summary row and expandable in place.
 *
 * Collapsed by default and on every group: the full dossier is ~45 questions, and opening the page
 * onto all of them buries the documents and the timeline under several screens of scrolling — which
 * is the opposite of "decide whether to invest an evening in this".
 *
 * A real `<button>` with `aria-expanded`, not a `<details>`: the summary is two lines of mixed
 * weight plus a count and a chevron, and styling `<summary>` to that shape fights the marker in
 * every browser. `min-h-11` holds the ~44px touch floor at 320px.
 */
function DossierGroup({ group }: { group: PreviewGroup }) {
  const { t } = useTranslation("acquire");
  const [isOpen, setIsOpen] = useState(false);

  const panelId = `dossier-group-${group.key}`;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
        /**
         * The open group carries a light ground and inset padding, so which one you are reading is
         * visible at a glance rather than inferred from the chevron.
         *
         * It matters most on the way back UP: the expanded list is tall enough that the header
         * scrolls out of view, and returning to it past two collapsed neighbours otherwise gives no
         * confirmation of which group you are inside. The `-mx-sm` keeps the collapsed rows flush
         * with the section's own padding while the open one appears to indent.
         */
        className={
          isOpen
            ? "-mx-sm flex min-h-11 w-[calc(100%+var(--spacing-sm)*2)] flex-col gap-0.5 rounded-md bg-brand-50 px-sm py-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            : "flex min-h-11 w-full flex-col gap-0.5 rounded-md py-2.5 text-left outline-none hover:bg-surface-page focus-visible:ring-2 focus-visible:ring-focus-ring"
        }
      >
        <span className="flex w-full items-center gap-xs">
          {/* The section title is the SERVER's — see PublicSchemaSectionSchema. */}
          <span className="min-w-0 flex-1 wrap-break-word text-sm-semibold text-(--text-primary)">
            {group.title}
          </span>
          <span className="shrink-0 text-xs-medium text-(--text-tertiary)">
            {t("preview.dossier.question-count", {
              count: group.questions.length,
            })}
          </span>
          <span aria-hidden className="shrink-0 text-(--text-tertiary)">
            <Icon name={isOpen ? "chevron-up" : "chevron-down"} size={18} />
          </span>
        </span>

        {/*
          The design draws a summary line under every group ("Validation client · Viabilité · …").
          Only the round-authored sections carry a title in the payload, so a group with no authored
          questions has nothing to summarise and the line is omitted.

          ⚠️ Do NOT close that gap with a hardcoded map of field key → heading. That is exactly the
          hand-mirror that broke eligibility when the reference manifest moved (rules/rounds.md) —
          it reads correctly right up until the server's list changes, and then it lies quietly.
        */}
        {group.subtitles.length > 0 ? (
          <span className="w-full wrap-break-word text-xs-regular text-(--text-tertiary)">
            {group.subtitles.join(" · ")}
          </span>
        ) : null}
      </button>

      {/* Unmounted when collapsed: nothing here holds state, so there is nothing to lose, and a
          ~45-row list is worth keeping out of the tree on a 3G phone. */}
      {isOpen ? (
        <div id={panelId} className="flex flex-col pb-sm">
          <ul className="flex flex-col">
            {group.questions
              .filter((question) => !question.isContextual)
              .map((question) => (
                <QuestionRow
                  key={question.key}
                  label={question.label}
                  isRequired={question.isRequired}
                />
              ))}
          </ul>
          {group.questions.some((question) => question.isContextual) ? (
            <div className="flex flex-col gap-xs border-t border-(--border-default) pt-md">
              <h3 className="text-sm-semibold text-(--text-primary)">
                {t("preview.dossier.context-title")}
              </h3>
              <ul className="flex flex-col">
                {group.questions
                  .filter((question) => question.isContextual)
                  .map((question) => (
                    <QuestionRow
                      key={question.key}
                      label={question.label}
                      isRequired={question.isRequired}
                    />
                  ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * « Le dossier complet » — every question the applicant will be asked, in the order the form asks
 * them, grouped as its four steps (US-E11-01 step 2).
 *
 * `groups` is `null` for a round that has not frozen its configuration. A `SCHEDULED` round has
 * real dates and no published questions, and saying so is the honest render — an empty list would
 * tell someone there is nothing to prepare, which is the opposite of true.
 */
export function DossierGroups({
  groups,
  questionCount,
}: {
  groups: PreviewGroup[] | null;
  questionCount: number;
}) {
  const { t } = useTranslation("acquire");

  return (
    <section className="flex flex-col gap-lg border-y border-(--border-default) bg-surface-card px-md pt-lg pb-xl sm:px-xl">
      <div className="flex flex-col gap-xxs">
        <h2 className="text-md-semibold text-(--text-primary)">
          {t("preview.dossier.title")}
        </h2>
        <p className="text-sm-regular text-(--text-secondary)">
          {groups === null
            ? t("preview.dossier.not-published")
            : t("preview.dossier.description", { count: questionCount })}
        </p>
      </div>

      {groups === null ? null : (
        <>
          <div className="flex flex-col divide-y divide-(--border-default)">
            {groups.map((group) => (
              <DossierGroup key={group.key} group={group} />
            ))}
          </div>

          <p className="text-xs-regular text-(--text-tertiary)">
            {t("preview.dossier.footnote")}
          </p>
        </>
      )}
    </section>
  );
}
